import { useContext, useMemo } from 'react';
import { Tooltip } from '@usealloy/component-library';

import { getDollarCurrencyRightAttributeValueMode } from 'flow/editor/components/thresholds/helpers';
import type { ThresholdResult } from 'flow/store/engine/types/workflowFeatures';
import { formatCurrencyIntl } from '~/helpers/i18n';

import { ContainerType } from '../../../../types';
import { RuleBuilderContext } from '../../../context';
import { ThresholdContext } from '../context';
import {
  StyledGreenDot,
  StyledOutputBadge,
  StyledOutputBadgeContainer,
  StyledOutputText,
} from './styles';

type ThresholdOutputValueProps = {
  thresholdResult: ThresholdResult;
};

const ThresholdOutputValue = ({
  thresholdResult,
}: ThresholdOutputValueProps) => {
  const { containerType } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);

  const dollarCurrencyRightAttributeValueMode = useMemo(() => (
    getDollarCurrencyRightAttributeValueMode(populatedThreshold.left)
  ), [populatedThreshold.left]);

  const outputValue = thresholdResult.is_tag_set !== null
    ? `${thresholdResult.is_tag_set ? 'Set' : 'Not Set'}`
    : `${thresholdResult.left_value ?? 'No output value'}`;

  const displayValue = useMemo(() => {
    if (!dollarCurrencyRightAttributeValueMode) return outputValue;
    // An avg/st_dev aggregation can return fractional minor units, and
    // formatCurrencyIntl rejects decimals, so round to whole minor units the same
    // way convertDollarValueToRaw stores them rather than truncating.
    const minorUnits = parseFloat(outputValue);
    if (Number.isNaN(minorUnits)) return outputValue;
    return formatCurrencyIntl(Math.round(minorUnits)).formattedNumber;
  }, [dollarCurrencyRightAttributeValueMode, outputValue]);

  const tooltipContent = thresholdResult.is_true
    ? `Satisfies logic: ${displayValue}`
    : `Does not satisfy logic: ${displayValue}`;

  return (
    <StyledOutputBadgeContainer>
      <StyledGreenDot isVisible={thresholdResult.is_true} />
      <Tooltip
        content={tooltipContent}
        side="top"
        style={{
          wordBreak: 'break-all',
        }}
      >
        {containerType !== ContainerType.ISLAND && (
          <StyledOutputBadge
            outline={false}
            satisfiesLogic={thresholdResult.is_true}
          >
            <StyledOutputText>
              {displayValue}
            </StyledOutputText>
          </StyledOutputBadge>
        )}
      </Tooltip>
    </StyledOutputBadgeContainer>
  );
};

export default ThresholdOutputValue;
